import { flowApi } from '.';
import { TAG_TYPES } from 'types/services';
import type {
  AcquireRecordLockResponse,
  AgendaRecordItem,
  AgendaRecordVersions,
  GetAgendaRecordReq,
  GetAgendaFilteredRecordReq,
  GetAgendaRecordRes,
  GetAgendaRecordsRes,
  HeartbeatReason,
  HeartbeatRecordLockResponse,
  JoinedFilteredAgendaRes,
  RecordsForViewLightRes,
  UpdateAgendaRecordRes,
  UpdateWithNestedListReq,
  UpdateWithNestedListRes,
  UserAgendaCalendarEvent,
} from 'types';
import { isRecordDeleted } from 'utils/agendaRecord/record.ts';
import { hydrateLightResponse } from 'utils/agendaRecord/agendaRecord';

type RecordLockArgs = {
  agendaId: string;
  recordId: string;
  lockToken: string;
};

type HeartbeatRecordLockArgs = RecordLockArgs & {
  reason: HeartbeatReason;
};

export const agendaRecordsService = flowApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    getAgendaViewRecords: builder.query<GetAgendaRecordsRes<AgendaRecordItem[]>, GetAgendaRecordReq>({
      query: ({ params, recordIds, skipViewConditions, evaluateVisualAttributes, valueFormItems, filters }) => ({
        url: `/Agendas/${params.agendaId}/View/${params.viewId}/Records/Light`,
        method: 'POST',
        body: {
          params: {
            Page: params.page,
            PageSize: params.pageSize,
            OrderBy: params.orderBy,
            OrderDesc: params.orderDesc,
            Search: params.search,
            FormItemId: params.formItemId,
          },
          skipViewConditions,
          evaluateVisualAttributes,
          recordIds,
          valueFormItems,
          filters,
        },
      }),
      transformResponse: (response: RecordsForViewLightRes) => hydrateLightResponse(response),
      providesTags: [{ type: TAG_TYPES.RECORDS, id: 'LIST' }],
    }),
    getAgendaViewFilteredRecords: builder.query<GetAgendaRecordsRes<JoinedFilteredAgendaRes[]>, GetAgendaFilteredRecordReq>({
      query: ({ params, skipViewConditions, evaluateVisualAttributes, valueFormItems, filters }) => ({
        url: `/Agendas/${params.agendaId}/View/${params.viewId}/Records/FilteredNested`,
        method: 'POST',
        body: {
          params: {
            Page: params.page,
            PageSize: params.pageSize,
            OrderBy: params.orderBy,
            OrderDesc: params.orderDesc,
            Search: params.search,
            FormItemId: params.formItemId,
          },
          skipViewConditions,
          evaluateVisualAttributes,
          valueFormItems,
          filters,
        },
      }),
      providesTags: [{ type: TAG_TYPES.RECORDS, id: 'FILTERED_LIST' }],
    }),
    getAgendaViewCalendarEvents: builder.query<UserAgendaCalendarEvent[], GetAgendaRecordReq & { windowStart?: string; windowEnd?: string }>({
      query: ({ params, skipViewConditions, recordIds, valueFormItems, filters, windowStart, windowEnd }) => ({
        url: `/Agendas/${params.agendaId}/View/${params.viewId}/CalendarEvents`,
        method: 'POST',
        body: {
          windowStart,
          windowEnd,
          params: {
            Page: params.page,
            PageSize: params.pageSize,
            OrderBy: params.orderBy,
            OrderDesc: params.orderDesc,
            Search: params.search,
            FormItemId: params.formItemId,
          },
          valueFormItems,
          recordIds,
          filters,
          skipViewConditions,
        },
      }),
      providesTags: [{ type: TAG_TYPES.RECORDS, id: 'CALENDAR_EVENTS' }],
    }),
    getAgendaRecord: builder.query<GetAgendaRecordRes, { agendaId: string; recordId: string }>({
      query: ({ agendaId, recordId }) => ({
        url: `/Agendas/${agendaId}/Records/${recordId}`,
        method: 'GET',
      }),
      providesTags: [{ type: TAG_TYPES.RECORDS, id: 'DETAIL' }],
    }),
    getAgendaRecordModal: builder.query<GetAgendaRecordRes, { agendaId: string; recordId: string }>({
      query: ({ agendaId, recordId }) => ({
        url: `/Agendas/${agendaId}/Records/${recordId}`,
        method: 'GET',
      }),
    }),
    getAgendaRecordsCreate: builder.query<GetAgendaRecordRes, { agendaId: string }>({
      query: ({ agendaId }) => ({
        url: `/Agendas/${agendaId}/Records/Create`,
        method: 'GET',
      }),
      providesTags: [{ type: TAG_TYPES.RECORDS, id: 'DETAIL' }],
    }),
    getAgendaRecordsVersions: builder.query<AgendaRecordVersions, { agendaId: string; recordId: string }>({
      query: ({ agendaId, recordId }) => ({
        url: `/Agendas/${agendaId}/Records/${recordId}/Versions`,
        method: 'GET',
      }),
      providesTags: [{ type: TAG_TYPES.RECORDS, id: 'DETAIL' }],
    }),
    getAgendaSpecificVersion: builder.query<GetAgendaRecordRes, { agendaId: string; recordId: string; versionId: string }>({
      query: ({ agendaId, recordId, versionId }) => ({
        url: `/Agendas/${agendaId}/Records/${recordId}/Versions/${versionId}`,
        method: 'GET',
      }),
      providesTags: [{ type: TAG_TYPES.RECORDS, id: 'DETAIL' }],
    }),
    updateAgendaRecordsVersion: builder.mutation({
      query: ({ agendaId, recordId, versionId }) => ({
        url: `/Agendas/${agendaId}/Records/${recordId}/Versions/${versionId}/Restore`,
        method: 'PATCH',
      }),
      invalidatesTags: [{ type: TAG_TYPES.RECORDS, id: 'DETAIL' }],
    }),
    createAgendaRecord: builder.mutation({
      query: ({ agendaId, body }) => ({
        url: `/Agendas/${agendaId}/Records`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [
        {
          type: TAG_TYPES.RECORDS,
          id: 'LIST',
        },
        {
          type: TAG_TYPES.RECORDS,
          id: 'DETAIL',
        },
        {
          type: TAG_TYPES.TABLE_DETAIL,
          id: 'LIST',
        },
      ],
    }),
    updateAgendaRecord: builder.mutation<UpdateAgendaRecordRes, unknown>({
      query: ({ agendaId, recordId, body, lockToken }) => ({
        url: `/Agendas/${agendaId}/Records/${recordId}`,
        method: 'PUT',
        body,
        ...(lockToken ? { headers: { 'X-Record-Lock-Token': lockToken } } : {}),
      }),
      invalidatesTags: [
        {
          type: TAG_TYPES.RECORDS,
          id: 'LIST',
        },
        {
          type: TAG_TYPES.TABLE_DETAIL,
          id: 'LIST',
        },
      ],
      onQueryStarted: async ({ recordId }, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;

          if (!isRecordDeleted(recordId, data.deleteRecordAction)) {
            dispatch(agendaRecordsService.util.invalidateTags([{ type: TAG_TYPES.RECORDS, id: 'DETAIL' }]));
          }
        } catch {
          // request failure is surfaced to the mutation caller
        }
      },
    }),
    runActionsOnAgendaRecord: builder.mutation<UpdateAgendaRecordRes, unknown>({
      query: ({ agendaId, recordId, body, lockToken }) => ({
        url: `/Agendas/${agendaId}/Records/${recordId}/RunActions`,
        method: 'PUT',
        body,
        ...(lockToken ? { headers: { 'X-Record-Lock-Token': lockToken } } : {}),
      }),
      invalidatesTags: [
        {
          type: TAG_TYPES.RECORDS,
          id: 'LIST',
        },
      ],
      onQueryStarted: async ({ recordId }, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;

          if (!isRecordDeleted(recordId, data.deleteRecordAction)) {
            dispatch(agendaRecordsService.util.invalidateTags([{ type: TAG_TYPES.RECORDS, id: 'DETAIL' }]));
          }
        } catch {
          // request failure is surfaced to the mutation caller
        }
      },
    }),
    updateWithNestedList: builder.mutation<UpdateWithNestedListRes, UpdateWithNestedListReq>({
      query: ({ agendaId, body, lockToken }) => ({
        url: `/Agendas/${agendaId}/Records/UpdateWithNestedList`,
        method: 'POST',
        body,
        ...(lockToken ? { headers: { 'X-Record-Lock-Token': lockToken } } : {}),
      }),
      invalidatesTags: [
        {
          type: TAG_TYPES.RECORDS,
          id: 'LIST',
        },
        { type: TAG_TYPES.VIEW, id: 'LIST' },
        {
          type: TAG_TYPES.TABLE_DETAIL,
          id: 'LIST',
        },
        { type: TAG_TYPES.RECORDS, id: 'DETAIL' },
      ],
    }),
    acquireRecordLock: builder.mutation<AcquireRecordLockResponse, { agendaId: string; recordId: string; suppressErrorToast?: boolean }>({
      query: ({ agendaId, recordId }) => ({
        url: `/Agendas/${agendaId}/Records/${recordId}/Lock`,
        method: 'POST',
      }),
    }),
    releaseRecordLock: builder.mutation<void, RecordLockArgs>({
      queryFn: async ({ agendaId, recordId, lockToken }, _api, _extraOptions, baseQuery) => {
        await baseQuery({
          url: `/Agendas/${agendaId}/Records/${recordId}/Lock`,
          method: 'DELETE',
          ...(lockToken ? { headers: { 'X-Record-Lock-Token': lockToken } } : {}),
        });

        return { data: undefined };
      },
    }),
    heartbeatRecordLock: builder.mutation<HeartbeatRecordLockResponse, HeartbeatRecordLockArgs>({
      query: ({ agendaId, recordId, lockToken, reason }) => ({
        url: `/Agendas/${agendaId}/Records/${recordId}/Lock/Heartbeat`,
        method: 'POST',
        body: { reason },
        ...(lockToken ? { headers: { 'X-Record-Lock-Token': lockToken } } : {}),
      }),
    }),
    calculateAgendaRecord: builder.mutation({
      query: ({ agendaId, body }) => ({
        url: `/Agendas/${agendaId}/Records/Calculate`,
        method: 'POST',
        body,
      }),
    }),
    deleteAgendaRecord: builder.mutation({
      query: ({ agendaId, recordId }) => ({
        url: `/Agendas/${agendaId}/Records/${recordId}`,
        method: 'DELETE',
      }),
      invalidatesTags: [
        {
          type: TAG_TYPES.RECORDS,
          id: 'LIST',
        },
        {
          type: TAG_TYPES.TABLE_DETAIL,
          id: 'LIST',
        },
      ],
    }),
    exportAgendaRecords: builder.mutation({
      query: (body) => ({
        url: 'Exports/Records',
        method: 'POST',
        body,
      }),
    }),
    exportAgendaRecordsZip: builder.mutation({
      query: (body) => ({
        url: 'Exports/Records/Zip',
        method: 'POST',
        body,
      }),
    }),
  }),
});
export const {
  useGetAgendaViewRecordsQuery,
  useGetAgendaViewFilteredRecordsQuery,
  useGetAgendaViewCalendarEventsQuery,
  useLazyGetAgendaViewRecordsQuery,
  useGetAgendaRecordQuery,
  useGetAgendaRecordModalQuery,
  useLazyGetAgendaRecordQuery,
  useGetAgendaRecordsCreateQuery,
  useGetAgendaRecordsVersionsQuery,
  useGetAgendaSpecificVersionQuery,
  useLazyGetAgendaSpecificVersionQuery,
  useUpdateAgendaRecordsVersionMutation,
  useLazyGetAgendaRecordsCreateQuery,
  useCreateAgendaRecordMutation,
  useRunActionsOnAgendaRecordMutation,
  useUpdateAgendaRecordMutation,
  useDeleteAgendaRecordMutation,
  useExportAgendaRecordsMutation,
  useExportAgendaRecordsZipMutation,
  useCalculateAgendaRecordMutation,
  useUpdateWithNestedListMutation,
  useAcquireRecordLockMutation,
  useReleaseRecordLockMutation,
  useHeartbeatRecordLockMutation,
} = agendaRecordsService;
